import { createFileRoute, Link } from "@tanstack/react-router";
import { useProjects } from "@/features/projects/hooks/useProjects";
import { useCustomers } from "@/features/customers/hooks/useCustomers";
import { useAllWorkers } from "@/features/workers/hooks/useWorkers";
import { useUsers } from "@/features/users/hooks/useUsers";
import { useConfig, useProjectStatuses } from "@/features/config/hooks/useConfig";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Users, Briefcase, HardHat, UserCheck, AlertTriangle, MessageCircle } from "lucide-react";
import { getProjectStatusAlerts } from "@/lib/projectStatusAlerts";
import { useAllWorkerMessages } from "@/features/workers/hooks/useWorkerMessages";
import { stripHtmlTags } from "@/lib/utils";

export const Route = createFileRoute("/dashboard/admin/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { projects, loading: projectsLoading } = useProjects();
  const { customers, loading: customersLoading } = useCustomers();
  const { workers, loading: workersLoading } = useAllWorkers();
  const { users, loading: usersLoading } = useUsers();
  const { loading: configLoading } = useConfig();
  const { projectStatuses, loading: statusesLoading } = useProjectStatuses();
  const { conversations, loading: messagesLoading, totalUnreadCount } = useAllWorkerMessages();

  const isLoading =
    projectsLoading ||
    customersLoading ||
    workersLoading ||
    usersLoading ||
    configLoading ||
    statusesLoading ||
    messagesLoading;

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
        <div className="grid auto-rows-min gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="bg-muted h-4 w-3/4 rounded"></div>
              </CardHeader>
              <CardContent>
                <div className="bg-muted mb-2 h-8 w-1/2 rounded"></div>
                <div className="bg-muted h-3 w-full rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="animate-pulse">
            <CardHeader>
              <div className="bg-muted h-5 w-1/3 rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="bg-muted h-4 rounded"></div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="animate-pulse">
            <CardHeader>
              <div className="bg-muted h-5 w-1/3 rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="bg-muted h-4 rounded"></div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const activeProjects = projects.filter((p) => p.isActive);
  const totalUsers = users.length;
  const managers = users.filter((u) => u.type === "manager");
  const admins = users.filter((u) => u.type === "admin");

  const recentProjects = projects
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
    .slice(0, 5);

  const recentCustomers = customers
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
    .slice(0, 5);

  // Get project status alerts
  const statusAlerts = getProjectStatusAlerts(projects, projectStatuses);

  return (
    <div className="flex flex-1 flex-col gap-4 p-1 pt-0 sm:p-4">
      {/* Project Status Alerts */}
      {statusAlerts.length > 0 && (
        <Card className="border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-950">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              <CardTitle className="text-orange-900 dark:text-orange-100">
                Project Status Alerts
              </CardTitle>
            </div>
            <CardDescription className="text-orange-700 dark:text-orange-300">
              {statusAlerts.length} {statusAlerts.length === 1 ? "project has" : "projects have"}{" "}
              exceeded status reminder thresholds
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {statusAlerts.slice(0, 5).map((alert) => (
                <Link
                  key={alert.project.id}
                  to={`/dashboard/admin/projects/$projectId`}
                  params={{ projectId: alert.project.id }}
                >
                  <Alert className="cursor-pointer border-orange-300 hover:bg-orange-100 dark:border-orange-800 dark:hover:bg-orange-900">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle className="font-semibold">{alert.project.name}</AlertTitle>
                    <AlertDescription>
                      <div className="mt-1 flex flex-col gap-1 text-sm">
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            style={{
                              borderColor: alert.statusColor,
                              color: alert.statusColor,
                            }}
                          >
                            {alert.statusName}
                          </Badge>
                          <span className="text-muted-foreground">
                            for <strong>{alert.daysSinceChange}</strong> days (threshold:{" "}
                            {alert.threshold} days)
                          </span>
                        </div>
                        <span className="font-medium text-orange-700 dark:text-orange-300">
                          {alert.daysOverThreshold} day{alert.daysOverThreshold !== 1 ? "s" : ""}{" "}
                          over threshold
                        </span>
                      </div>
                    </AlertDescription>
                  </Alert>
                </Link>
              ))}
              {statusAlerts.length > 5 && (
                <p className="text-muted-foreground text-center text-sm">
                  +{statusAlerts.length - 5} more{" "}
                  <Link to="/dashboard/admin/projects" className="underline">
                    View all projects
                  </Link>
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid auto-rows-min gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
            <Briefcase className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{projects.length}</div>
            <p className="text-muted-foreground text-xs">{activeProjects.length} active</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Customers</CardTitle>
            <Users className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{customers.length}</div>
            <p className="text-muted-foreground text-xs">Total customers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Workers</CardTitle>
            <HardHat className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{workers.length}</div>
            <p className="text-muted-foreground text-xs">Active workers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <UserCheck className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUsers}</div>
            <p className="text-muted-foreground text-xs">
              {managers.length} managers, {admins.length} admins
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Recent Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentProjects.length > 0 ? (
                recentProjects.map((project) => (
                  <Link
                    to={`/dashboard/admin/projects/$projectId`}
                    params={{ projectId: project.id }}
                    key={project.id}
                  >
                    <div key={project.id} className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <p className="text-sm font-medium">{project.name}</p>
                        <p className="text-muted-foreground line-clamp-2 text-xs">
                          {stripHtmlTags(project.description) || "No description"}
                        </p>
                      </div>
                      <Badge variant={project.isActive ? "default" : "secondary"}>
                        {project.isActive ? "Active" : "Completed"}
                      </Badge>
                    </div>
                  </Link>
                ))
              ) : (
                <p className="text-muted-foreground text-sm">No projects yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Customers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentCustomers.length > 0 ? (
                recentCustomers.map((customer) => (
                  <Link
                    to={`/dashboard/admin/customers/$customerId`}
                    params={{ customerId: customer.uid }}
                    key={customer.uid}
                  >
                    <div key={customer.uid} className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <p className="text-sm font-medium">{customer.name}</p>
                        <p className="text-muted-foreground text-xs">{customer.email}</p>
                      </div>
                      <Badge variant="outline">Customer</Badge>
                    </div>
                  </Link>
                ))
              ) : (
                <p className="text-muted-foreground text-sm">No customers yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-green-600" />
                <CardTitle>Worker Messages</CardTitle>
              </div>
              {totalUnreadCount > 0 && <Badge className="bg-green-600">{totalUnreadCount}</Badge>}
            </div>
            <CardDescription>Recent messages from workers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {conversations.filter((c) => c.unreadCount > 0).slice(0, 5).length > 0 ? (
                <>
                  {conversations
                    .filter((c) => c.unreadCount > 0)
                    .slice(0, 5)
                    .map((conversation) => (
                      <Link
                        to="/dashboard/admin/worker_messages/$workerId"
                        params={{ workerId: conversation.workerId }}
                        key={conversation.workerId}
                      >
                        <div className="flex items-center justify-between rounded-lg p-2 transition-colors hover:bg-gray-50">
                          <div className="flex flex-col">
                            <p className="text-sm font-medium">{conversation.workerName}</p>
                            <p className="text-muted-foreground line-clamp-1 text-xs">
                              {conversation.messages[conversation.messages.length - 1]?.text}
                            </p>
                          </div>
                          <Badge variant="default" className="bg-green-600">
                            {conversation.unreadCount}
                          </Badge>
                        </div>
                      </Link>
                    ))}
                  <Link
                    to="/dashboard/admin/worker_messages"
                    className="text-primary block text-center text-sm hover:underline"
                  >
                    View all messages →
                  </Link>
                </>
              ) : (
                <p className="text-muted-foreground text-center text-sm">No unread messages</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
